import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";

const REASONS = [
  { code: "customer_requested", label: "Customer requested" },
  { code: "wrong_manifest", label: "LR wrongly manifested (wrong address)" },
  { code: "rescheduled_tomorrow", label: "Customer rescheduled to tomorrow" },
  { code: "other", label: "Other" },
];

/** Cancel the raised Delhivery pickup of one shipment, with a reason. The shipment goes back to "pickup needed". */
export default function PickupCancelDialog({ shipment, onClose, onCancelled }) {
  const [code, setCode] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ready = code && (code !== "other" || text.trim());

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/v2/manifest-gate/pickup-desk/${shipment.id}/cancel`, { reason_code: code, reason_text: text.trim() || null });
      toast.success("Pickup cancelled - you can recreate it when the customer is ready");
      await onCancelled();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(d && typeof d === "object" ? d : { title: "Pickup not cancelled", message: d || "Something went wrong - please try again." });
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="pickup-cancel-dialog">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-slate-800">Cancel pickup - {shipment.order_id}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-2">
          <p className="text-xs text-slate-600">Why is the pickup being cancelled?</p>
          {REASONS.map((r) => (
            <label key={r.code} className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer ${code === r.code ? "border-red-400 bg-red-50" : ""}`}>
              <input type="radio" name="cancel-reason" checked={code === r.code} onChange={() => setCode(r.code)} data-testid={`cancel-reason-${r.code}`} />
              {r.label}
            </label>
          ))}
          {code === "other" && (
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={200} placeholder="Type the reason"
              className="w-full border rounded-lg px-2.5 py-1.5 text-sm" data-testid="cancel-reason-text" />
          )}
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Delhivery has no cancel option for us: also cancel this pickup request in the Delhivery portal.
          </p>
          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2"><b>{err.title}</b><div>{err.message}</div></div>}
        </div>
        <div className="flex gap-2 justify-end p-4 border-t">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded-lg">Keep pickup</button>
          <button onClick={submit} disabled={!ready || busy} data-testid="pickup-cancel-confirm"
            className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-red-600 text-white disabled:opacity-50 flex items-center gap-1">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Cancel pickup
          </button>
        </div>
      </div>
    </div>
  );
}
